import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SearchIcon from '@mui/icons-material/Search';
import MaterialSymbol, { toolbarIconButtonSx } from '../components/schedules/MaterialSymbol';
import ScheduleTaskTable from '../components/schedules/ScheduleTaskTable';
import ScheduleGantt from '../components/schedules/ScheduleGantt';
import {
  addScheduleTask,
  deleteScheduleTask,
  getSchedule,
  replaceScheduleTasks,
  updateScheduleHeader,
  type ScheduleDetail,
  type ScheduleTask,
} from '../services/schedulesService';
import {
  exportToCsv,
  exportToPdf,
  exportToXlsx,
  importFromFile,
} from '../components/schedules/scheduleIo';
import {
  normalizeOutline,
  resolveDependencies,
} from '../components/schedules/schedulingEngine';

type ViewTab = 'tabela' | 'gantt';

const SAVE_DEBOUNCE_MS = 800;

const ScheduleDetailPage: React.FC = () => {
  const { projectId, id } = useParams<{ projectId: string; id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [detail, setDetail] = React.useState<ScheduleDetail | null>(null);
  const [tasks, setTasks] = React.useState<ScheduleTask[]>([]);
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [view, setView] = React.useState<ViewTab>('tabela');
  const [search, setSearch] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });

  const [exportAnchor, setExportAnchor] = React.useState<HTMLElement | null>(null);
  const [importBusy, setImportBusy] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const saveTimerRef = React.useRef<number | null>(null);

  const [renameDialogOpen, setRenameDialogOpen] = React.useState(false);
  const [renameDraft, setRenameDraft] = React.useState('');
  const [stateDateDraft, setStateDateDraft] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await getSchedule(id!);
        if (cancelled) return;
        setDetail(data);
        setTasks(resolveDependencies(normalizeOutline(data.tasks)));
        setRenameDraft(data.name);
        setStateDateDraft(data.state_date || '');
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Błąd wczytywania');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  /* Debounced save — po każdej lokalnej zmianie `tasks` (dirty) zapisujemy do backendu. */
  React.useEffect(() => {
    if (!dirty || !id) return;
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(async () => {
      setSaving(true);
      try {
        const saved = await replaceScheduleTasks(
          id,
          tasks.map((t) => ({
            outline_level: t.outline_level || 0,
            ssp: t.ssp,
            opis: t.opis,
            days: t.days,
            start_date: t.start_date,
            end_date: t.end_date,
            predecessor: t.predecessor || undefined,
            value: t.value,
            notes: t.notes || undefined,
            progress_pct: t.progress_pct,
            progress_date: t.progress_date || undefined,
          }))
        );
        setTasks(saved);
        setDirty(false);
      } catch (err) {
        console.error('Zapis harmonogramu nie powiódł się:', err);
        setSnackbar({ open: true, message: 'Nie udało się zapisać zmian.' });
      } finally {
        setSaving(false);
      }
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  }, [dirty, tasks, id]);

  const filteredTasks = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter(
      (t) =>
        String(t.ordinal).includes(q) ||
        (t.ssp || '').toLowerCase().includes(q) ||
        (t.opis || '').toLowerCase().includes(q) ||
        (t.notes || '').toLowerCase().includes(q) ||
        (t.predecessor || '').toLowerCase().includes(q)
    );
  }, [tasks, search]);

  /**
   * Stosuje transformację do bieżącej listy zadań:
   *   - normalizuje outline_level (nie więcej niż prev+1),
   *   - przelicza daty wg poprzedników (FS/SS/FF/SF + lag),
   *   - ustawia dirty → debounce zapisze zmiany do backendu.
   */
  const applyChanges = React.useCallback((producer: (prev: ScheduleTask[]) => ScheduleTask[]) => {
    setTasks((prev) => {
      const produced = producer(prev);
      const normalized = normalizeOutline(produced);
      const resolved = resolveDependencies(normalized);
      return resolved;
    });
    setDirty(true);
  }, []);

  const handleIndent = React.useCallback((taskId: number) => {
    applyChanges((prev) => {
      const idx = prev.findIndex((t) => t.id === taskId);
      if (idx <= 0) return prev;
      const prevLevel = prev[idx - 1].outline_level || 0;
      const currentLevel = prev[idx].outline_level || 0;
      if (currentLevel >= prevLevel + 1) return prev;
      const next = prev.slice();
      next[idx] = { ...next[idx], outline_level: currentLevel + 1 };
      return next;
    });
  }, [applyChanges]);

  const handleOutdent = React.useCallback((taskId: number) => {
    applyChanges((prev) => {
      const idx = prev.findIndex((t) => t.id === taskId);
      if (idx < 0) return prev;
      const currentLevel = prev[idx].outline_level || 0;
      if (currentLevel === 0) return prev;
      const next = prev.slice();
      next[idx] = { ...next[idx], outline_level: currentLevel - 1 };
      return next;
    });
  }, [applyChanges]);

  /** Łączy zaznaczone zadania w łańcuch FS (poprzedni → następny). */
  const handleLinkSelected = React.useCallback(() => {
    if (selectedIds.length < 2) {
      setSnackbar({ open: true, message: 'Zaznacz co najmniej 2 zadania, żeby je połączyć.' });
      return;
    }
    applyChanges((prev) => {
      /* Zaznaczone idą w kolejności występowania w liście (po `ordinal`). */
      const ordered = prev
        .filter((t) => selectedIds.includes(t.id))
        .sort((a, b) => a.ordinal - b.ordinal);
      const byId = new Map(prev.map((t) => [t.id, { ...t }]));
      for (let i = 1; i < ordered.length; i++) {
        const prevOrdinal = ordered[i - 1].ordinal;
        const curr = byId.get(ordered[i].id)!;
        curr.predecessor = String(prevOrdinal);
      }
      return prev.map((t) => byId.get(t.id) || t);
    });
  }, [selectedIds, applyChanges]);

  /** Usuwa poprzedniki w zaznaczonych zadaniach. */
  const handleUnlinkSelected = React.useCallback(() => {
    if (!selectedIds.length) return;
    applyChanges((prev) =>
      prev.map((t) => (selectedIds.includes(t.id) ? { ...t, predecessor: null } : t))
    );
  }, [selectedIds, applyChanges]);

  const handleAddTask = React.useCallback(async () => {
    if (!id) return;
    const today = new Date().toISOString().slice(0, 10);
    try {
      const created = await addScheduleTask(id, {
        ssp: '',
        opis: 'Nowe zadanie',
        days: 1,
        start_date: today,
      });
      setTasks((prev) => [...prev, created]);
    } catch (err) {
      setSnackbar({ open: true, message: 'Nie udało się dodać zadania.' });
    }
  }, [id]);

  const handleDeleteTask = React.useCallback(
    async (taskId: number) => {
      if (!id) return;
      try {
        await deleteScheduleTask(id, taskId);
        /* Pobieramy świeżą listę (backend kompaktuje ordinal). */
        const fresh = await getSchedule(id);
        setTasks(fresh.tasks);
      } catch (err) {
        setSnackbar({ open: true, message: 'Nie udało się usunąć zadania.' });
      }
    },
    [id]
  );

  const handleExport = (format: 'csv' | 'xlsx' | 'pdf') => {
    if (!detail) return;
    const name = `${detail.project?.project_name || 'Projekt'} — ${detail.name}`;
    if (format === 'csv') exportToCsv(name, tasks);
    if (format === 'xlsx') exportToXlsx(name, tasks);
    if (format === 'pdf') exportToPdf(name, tasks);
    setExportAnchor(null);
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !id) return;
    setImportBusy(true);
    try {
      const parsed = await importFromFile(file);
      if (parsed.length === 0) {
        setSnackbar({ open: true, message: 'Nie znaleziono wierszy do zaimportowania.' });
        return;
      }
      const saved = await replaceScheduleTasks(id, parsed);
      setTasks(saved);
      setDirty(false);
      setSnackbar({ open: true, message: `Zaimportowano ${saved.length} zadań.` });
    } catch (err) {
      console.error('Import nieudany', err);
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Import nieudany.' });
    } finally {
      setImportBusy(false);
    }
  };

  const handleSaveHeader = async () => {
    if (!id) return;
    try {
      const updated = await updateScheduleHeader(id, {
        name: renameDraft.trim() || detail?.name,
        state_date: stateDateDraft || null,
      });
      setDetail((prev) => (prev ? { ...prev, ...updated } : prev));
      setRenameDialogOpen(false);
      setSnackbar({ open: true, message: 'Zapisano nagłówek harmonogramu.' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Nie udało się zapisać nagłówka.' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (loadError || !detail) {
    return (
      <Box sx={{ p: 3, color: '#c62828', fontFamily: 'Roboto, sans-serif' }}>
        {loadError || 'Harmonogram nie istnieje.'}
      </Box>
    );
  }

  const totalValue = tasks.reduce((acc, t) => acc + (Number(t.value) || 0), 0);
  const totalValueFmt = totalValue.toLocaleString('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: '#FAFAFA',
      }}
    >
      {/* Toolbar (górny pasek akcji — padding-bottom uwzględnia downbar 40px) */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'nowrap',
          gap: 1,
          px: 1.5,
          py: 0.5,
          borderBottom: '1px solid #e8eaed',
          bgcolor: '#FFFFFF',
          minHeight: 40,
          overflowX: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
          <Tooltip title="Dodaj zadanie">
            <IconButton size="small" onClick={handleAddTask} sx={toolbarIconButtonSx}>
              <MaterialSymbol name="add" color="#1A73E8" />
            </IconButton>
          </Tooltip>

          <Box sx={{ width: '1px', height: 24, bgcolor: '#e8eaed', mx: 0.25 }} />

          <Tooltip title="Importuj z pliku (CSV lub XLSX)">
            <span>
              <IconButton
                size="small"
                disabled={importBusy}
                onClick={() => fileInputRef.current?.click()}
                sx={toolbarIconButtonSx}
              >
                <MaterialSymbol name="upload" />
              </IconButton>
            </span>
          </Tooltip>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleImportFile}
          />

          <Tooltip title="Eksport: CSV, XLSX, PDF">
            <IconButton
              size="small"
              onClick={(e) => setExportAnchor(e.currentTarget)}
              sx={toolbarIconButtonSx}
            >
              <MaterialSymbol name="download" />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={exportAnchor} open={!!exportAnchor} onClose={() => setExportAnchor(null)}>
            <MenuItem onClick={() => handleExport('xlsx')}>Eksportuj XLSX</MenuItem>
            <MenuItem onClick={() => handleExport('csv')}>Eksportuj CSV</MenuItem>
            <MenuItem onClick={() => handleExport('pdf')}>Eksportuj PDF</MenuItem>
          </Menu>

          <Box sx={{ width: '1px', height: 24, bgcolor: '#e8eaed', mx: 0.25 }} />

          <Tooltip title="Cofnij wcięcie (Shift+Tab w kolumnie Opis)">
            <span>
              <IconButton
                size="small"
                disabled={selectedIds.length === 0}
                onClick={() => selectedIds.forEach(handleOutdent)}
                sx={toolbarIconButtonSx}
              >
                <MaterialSymbol name="format_indent_decrease" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Wcięcie — zmień w poddział (Tab w kolumnie Opis)">
            <span>
              <IconButton
                size="small"
                disabled={selectedIds.length === 0}
                onClick={() => selectedIds.forEach(handleIndent)}
                sx={toolbarIconButtonSx}
              >
                <MaterialSymbol name="format_indent_increase" />
              </IconButton>
            </span>
          </Tooltip>

          <Box sx={{ width: '1px', height: 24, bgcolor: '#e8eaed', mx: 0.25 }} />

          <Tooltip title="Połącz zaznaczone — łańcuch FS (poprzedni → następny)">
            <span>
              <IconButton
                size="small"
                disabled={selectedIds.length < 2}
                onClick={handleLinkSelected}
                sx={toolbarIconButtonSx}
              >
                <MaterialSymbol name="link" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Rozłącz — usuń poprzedniki zaznaczonych zadań">
            <span>
              <IconButton
                size="small"
                disabled={selectedIds.length === 0}
                onClick={handleUnlinkSelected}
                sx={toolbarIconButtonSx}
              >
                <MaterialSymbol name="link_off" />
              </IconButton>
            </span>
          </Tooltip>

          <Box sx={{ width: '1px', height: 24, bgcolor: '#e8eaed', mx: 0.25 }} />

          <Tooltip title="Edytuj nagłówek (nazwa, data stanu)">
            <IconButton size="small" onClick={() => setRenameDialogOpen(true)} sx={toolbarIconButtonSx}>
              <MaterialSymbol name="edit" />
            </IconButton>
          </Tooltip>

          {saving ? (
            <Typography sx={{ ml: 1, fontSize: 12, color: '#5F6368', whiteSpace: 'nowrap' }}>
              zapisywanie…
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', flexShrink: 0 }}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Szukaj"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: 220,
              fontFamily: 'Roboto, sans-serif',
              '& .MuiOutlinedInput-root': { height: 32, fontSize: 14 },
              '& .MuiOutlinedInput-input': { py: 0 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#595966', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {/* Obszar widoku — pad-bottom na wysokość downbara (40px). */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          paddingBottom: '40px',
        }}
      >
        {/* Oba widoki zamontowane — przełączane display:none, żeby scroll i stan Gantta były stabilne. */}
        <Box
          role="tabpanel"
          hidden={view !== 'tabela'}
          sx={{
            flex: 1,
            minHeight: 0,
            display: view === 'tabela' ? 'flex' : 'none',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <ScheduleTaskTable
            tasks={filteredTasks}
            onChange={(next) => {
              /* `next` zawiera tylko przefiltrowane wiersze — musimy je złączyć z pozostałymi. */
              const byId = new Map(next.map((t) => [t.id, t]));
              applyChanges((prev) => prev.map((t) => byId.get(t.id) || t));
            }}
            onDelete={handleDeleteTask}
            onAddBelow={handleAddTask}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            onIndent={handleIndent}
            onOutdent={handleOutdent}
          />
        </Box>
        <Box
          role="tabpanel"
          hidden={view !== 'gantt'}
          sx={{
            flex: 1,
            minHeight: 0,
            display: view === 'gantt' ? 'flex' : 'none',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <ScheduleGantt tasks={filteredTasks} stateDate={detail.state_date} />
        </Box>
      </Box>

      {/* Downbar — styl analogiczny do EasyEstimateEstimateShell. */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '40px',
          display: 'flex',
          alignItems: 'stretch',
          bgcolor: '#d9d9d9',
          borderTop: '1px solid #c4c4c4',
          boxShadow: '0 -1px 4px rgba(0, 0, 0, 0.12)',
          zIndex: 5,
          pl: 0.5,
          pr: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Tooltip title="Powrót do listy harmonogramów" placement="top">
            <span>
              <IconButton
                size="small"
                aria-label="Powrót do harmonogramów projektu"
                onClick={() =>
                  navigate(`/app/schedules/project/${encodeURIComponent(projectId || '')}`)
                }
                sx={{
                  flexShrink: 0,
                  color: '#020242',
                  p: '4px',
                  borderRadius: '4px',
                  '&:hover': { bgcolor: 'rgba(2, 2, 66, 0.06)' },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box
          aria-hidden
          sx={{
            width: '1px',
            flexShrink: 0,
            alignSelf: 'stretch',
            my: 1,
            mx: 1,
            bgcolor: 'rgba(2, 2, 66, 0.18)',
          }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minWidth: 0,
            flex: '0 1 auto',
            maxWidth: 'min(560px, 42vw)',
            overflow: 'hidden',
            py: 0.25,
          }}
          aria-label="Nagłówek harmonogramu"
        >
          <Typography
            component="span"
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '11px',
              fontWeight: 600,
              color: '#020242',
              lineHeight: 1.15,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {detail.project?.project_name || 'Projekt'}
          </Typography>
          <Typography
            component="span"
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '10px',
              fontWeight: 400,
              color: '#5F6368',
              lineHeight: 1.15,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Harmonogram: {detail.name}
            {detail.state_date ? ` · Data stanu: ${detail.state_date}` : ''}
          </Typography>
        </Box>

        <Box
          aria-hidden
          sx={{
            width: '1px',
            flexShrink: 0,
            alignSelf: 'stretch',
            my: 1,
            mx: 1,
            bgcolor: 'rgba(2, 2, 66, 0.18)',
          }}
        />

        <Tabs
          value={view === 'tabela' ? 0 : 1}
          onChange={(_, v) => setView(v === 0 ? 'tabela' : 'gantt')}
          aria-label="Harmonogram — widok"
          sx={{
            flexShrink: 0,
            alignSelf: 'stretch',
            minHeight: '40px',
            '& .MuiTabs-flexContainer': {
              alignItems: 'center',
              minHeight: '40px',
            },
            '& .MuiTab-root': {
              minHeight: '40px',
              textTransform: 'none',
              fontSize: '14px',
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 400,
              minWidth: 'auto',
              px: 1,
              py: 0,
              mx: 0.5,
              color: '#020242',
              position: 'relative',
              '&.Mui-selected': {
                color: '#020242',
                fontWeight: 500,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '6px',
                  left: '8px',
                  right: '8px',
                  height: '2px',
                  backgroundColor: '#020242',
                },
              },
              '&:hover': { backgroundColor: 'transparent' },
            },
            '& .MuiTabs-indicator': { display: 'none' },
          }}
        >
          <Tab label="Tabela" />
          <Tab label="Wykres Gantta" />
        </Tabs>

        <Box sx={{ flex: 1, minWidth: 0 }} aria-hidden />

        <Box sx={{ display: 'flex', alignItems: 'stretch', flexShrink: 0 }}>
          <Box
            aria-hidden
            sx={{
              width: '1px',
              flexShrink: 0,
              alignSelf: 'stretch',
              my: 1,
              mx: 1,
              bgcolor: 'rgba(2, 2, 66, 0.18)',
            }}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              color: '#c53030',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              pr: 1.5,
            }}
          >
            Wartość netto: {totalValueFmt} zł
          </Box>
          <Box
            aria-hidden
            sx={{
              width: '1px',
              flexShrink: 0,
              alignSelf: 'stretch',
              my: 1,
              mx: 1,
              bgcolor: 'rgba(2, 2, 66, 0.18)',
            }}
          />
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              alignSelf: 'center',
              backgroundColor: 'rgba(2, 2, 66, 0.08)',
              color: '#020242',
              border: '1px solid rgba(2, 2, 66, 0.22)',
              borderRadius: 1,
              px: 1,
              py: 0.5,
              fontSize: '0.75rem',
              fontWeight: 500,
              fontFamily: 'Roboto, sans-serif',
              lineHeight: 1.2,
            }}
            title="Liczba zadań w harmonogramie"
          >
            Zadań: {tasks.length}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>Edytuj harmonogram</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 360 }}>
          <TextField
            label="Nazwa"
            size="small"
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            autoFocus
            fullWidth
            sx={{ mt: 1 }}
          />
          <TextField
            label="Data stanu"
            size="small"
            type="date"
            value={stateDateDraft}
            onChange={(e) => setStateDateDraft(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Anuluj</Button>
          <Button variant="contained" onClick={handleSaveHeader}>
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduleDetailPage;
